import { z } from "zod";
import { optionalCpfSchema } from "../common/document";
import { optionalPhoneBRSchema } from "../common/phone";
import { optionalCepSchema } from "../common/cep";
import { brazilianStateSchema } from "../user/cro";

export const sexSchema = z.enum(["M", "F", "OTHER"]);
export type Sex = z.infer<typeof sexSchema>;

/** Base — todos os campos opcionais exceto `name`. */
const patientBaseSchema = z.object({
  name: z.string().trim().min(2, "Nome muito curto").max(120),
  identifier: z.string().trim().max(50).optional().or(z.literal("").transform(() => undefined)),
  clinicalRecordNumber: z.string().trim().max(50).optional().or(z.literal("").transform(() => undefined)),
  document: optionalCpfSchema,
  sex: sexSchema.optional(),
  birthDate: z.coerce.date().optional().nullable(),
  phone: optionalPhoneBRSchema,
  landline: optionalPhoneBRSchema,
  /** WhatsApp, quando é outro número — vazio = o celular. */
  whatsapp: optionalPhoneBRSchema,
  contatoDeEmergenciaNome: z.string().trim().max(120).optional().or(z.literal("").transform(() => undefined)),
  contatoDeEmergenciaTelefone: optionalPhoneBRSchema,
  email: z
    .string()
    .trim()
    .toLowerCase()
    .email()
    .optional()
    .or(z.literal("").transform(() => undefined)),
  zipCode: optionalCepSchema,
  address: z.string().trim().max(200).optional().or(z.literal("").transform(() => undefined)),
  addressNumber: z.string().trim().max(10).optional().or(z.literal("").transform(() => undefined)),
  addressComplement: z.string().trim().max(100).optional().or(z.literal("").transform(() => undefined)),
  neighborhood: z.string().trim().max(100).optional().or(z.literal("").transform(() => undefined)),
  city: z.string().trim().max(100).optional().or(z.literal("").transform(() => undefined)),
  state: brazilianStateSchema.optional(),
  profession: z.string().trim().max(100).optional().or(z.literal("").transform(() => undefined)),
  motherName: z.string().trim().max(120).optional().or(z.literal("").transform(() => undefined)),
  insurancePlanName: z.string().trim().max(120).optional().or(z.literal("").transform(() => undefined)),
  insurancePlanNumber: z.string().trim().max(50).optional().or(z.literal("").transform(() => undefined)),
  avatarUrl: z.string().startsWith("data:image/").max(2_800_000).nullable().optional(),
  tags: z.array(z.string().trim().max(50)).max(20).default([]),
  howDidMeet: z.string().trim().max(100).optional().or(z.literal("").transform(() => undefined)),
  notes: z.string().trim().max(5000).optional().or(z.literal("").transform(() => undefined)),
  active: z.boolean().default(true),
});

export const patientCreateSchema = patientBaseSchema.extend({
  /** Cadastra o mesmo paciente em todas as organizações ativas do usuário, não só na atual. */
  addToAllOrganizations: z.boolean().default(false),
  /** Com addToAllOrganizations, restringe A QUAIS organizações cadastrar (escolha no dialog).
   *  Ausente ou vazio = todas, como antes. A organização ativa entra sempre. */
  organizationIds: z.array(z.string().min(1)).max(50).optional(),
  /** Estrangeiro, sem CPF: dispensa o CPF quando a clínica o exige. */
  semCpf: z.boolean().optional(),
});
/** Campos que a ficha deixa apagar: no update, `null` limpa (vazio continua sendo "não mexe"). */
const CAMPOS_QUE_LIMPAM = [
  "identifier",
  "clinicalRecordNumber",
  "document",
  "sex",
  "phone",
  "landline",
  "whatsapp",
  "contatoDeEmergenciaNome",
  "contatoDeEmergenciaTelefone",
  "email",
  "zipCode",
  "address",
  "addressNumber",
  "addressComplement",
  "neighborhood",
  "city",
  "state",
  "profession",
  "motherName",
  "insurancePlanName",
  "insurancePlanNumber",
  "howDidMeet",
  "notes",
] as const;
export const patientUpdateSchema = patientBaseSchema
  .partial()
  .extend(Object.fromEntries(CAMPOS_QUE_LIMPAM.map((k) => [k, patientBaseSchema.shape[k].nullable().optional()])) as {
    [K in (typeof CAMPOS_QUE_LIMPAM)[number]]: z.ZodOptional<z.ZodNullable<(typeof patientBaseSchema.shape)[K]>>;
  })
  .extend({
    // Sem o default: no zod 4 o default vale mesmo com o campo ausente, e editar sem mandar o campo o reescreveria.
    active: patientBaseSchema.shape.active.unwrap().optional(),
    tags: patientBaseSchema.shape.tags.unwrap().optional(),
  });

export type PatientCreateInput = z.infer<typeof patientCreateSchema>;
export type PatientUpdateInput = z.infer<typeof patientUpdateSchema>;

/** Filtros da listagem de pacientes. */
export const patientListFiltersSchema = z.object({
  search: z.string().trim().max(200).optional(),
  active: z.boolean().optional(),
  tag: z.string().optional(),
  sortBy: z.enum(["name", "createdAt"]).default("name"),
  order: z.enum(["asc", "desc"]).default("asc"),
  take: z.number().int().min(1).max(500).default(50),
  skip: z.number().int().min(0).default(0),
});

export type PatientListFilters = z.infer<typeof patientListFiltersSchema>;

/** O que a planilha de pacientes pode levar. O servidor lê só os campos pedidos: quem desmarca
 *  CPF no diálogo não recebe CPF. */
export const CAMPOS_DA_EXPORTACAO_DE_PACIENTES = [
  "name",
  "document",
  "identifier",
  "birthDate",
  "sex",
  "phone",
  "email",
  "city",
  "state",
  "tags",
  "active",
  "createdAt",
] as const;
export type CampoDaExportacaoDePacientes = (typeof CAMPOS_DA_EXPORTACAO_DE_PACIENTES)[number];

/** Cabeçalho de cada campo na planilha — o mesmo nome que a importação reconhece (por isso "Celular"
 *  e não "Telefone"). Também é o que a auditoria registra como colunas exportadas. */
export const ROTULO_DO_CAMPO_DE_PACIENTE: Record<CampoDaExportacaoDePacientes, string> = {
  name: "Nome",
  document: "CPF",
  identifier: "RG / Documento",
  birthDate: "Data de nascimento",
  sex: "Sexo",
  phone: "Celular",
  email: "E-mail",
  city: "Cidade",
  state: "UF",
  tags: "Etiquetas",
  active: "Ativo",
  createdAt: "Cadastrado em",
};

/** Mesmos filtros e ordem da lista, sem paginação: o servidor busca até o fim. */
export const patientExportarSchema = patientListFiltersSchema.omit({ take: true, skip: true }).extend({
  campos: z.array(z.enum(CAMPOS_DA_EXPORTACAO_DE_PACIENTES)).min(1).max(CAMPOS_DA_EXPORTACAO_DE_PACIENTES.length),
});
export type PatientExportarInput = z.infer<typeof patientExportarSchema>;

// ─── Convite público de cadastro ─────────────────────

export const patientInviteCreateSchema = z.object({
  /** Quantos dias antes de expirar. Default 7. */
  expiresInDays: z.number().int().min(1).max(90).default(7),
  email: z
    .string()
    .trim()
    .toLowerCase()
    .email()
    .optional()
    .or(z.literal("").transform(() => undefined)),
  phone: optionalPhoneBRSchema,
});
export type PatientInviteCreateInput = z.infer<typeof patientInviteCreateSchema>;

/** Schema público de aceitação — paciente preenche no /p/:token.
 *  Validação permissiva: paciente pode ser estrangeiro (documento ≠ CPF) ou
 *  informar dados parciais. A clínica refina no CRM depois. */
export const patientInviteAcceptSchema = z.object({
  token: z.string().min(1),
  name: z.string().trim().min(2, "Informe seu nome").max(120),
  document: z
    .string()
    .trim()
    .max(30)
    .optional()
    .or(z.literal("").transform(() => undefined)),
  sex: sexSchema.optional(),
  birthDate: z.coerce.date().optional().nullable(),
  phone: z
    .string()
    .trim()
    .max(20)
    .optional()
    .transform((v) => {
      if (!v) return undefined;
      const d = v.replace(/\D/g, "");
      return d.length >= 10 && d.length <= 11 ? d : undefined;
    }),
  email: z
    .string()
    .trim()
    .toLowerCase()
    .optional()
    .transform((v) => {
      if (!v) return undefined;
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? v : undefined;
    }),
  zipCode: z
    .string()
    .trim()
    .optional()
    .transform((v) => {
      if (!v) return undefined;
      const d = v.replace(/\D/g, "");
      return d.length === 8 ? d : undefined;
    }),
  address: z.string().trim().max(200).optional().or(z.literal("").transform(() => undefined)),
  addressNumber: z.string().trim().max(10).optional().or(z.literal("").transform(() => undefined)),
  addressComplement: z.string().trim().max(100).optional().or(z.literal("").transform(() => undefined)),
  neighborhood: z.string().trim().max(100).optional().or(z.literal("").transform(() => undefined)),
  city: z.string().trim().max(100).optional().or(z.literal("").transform(() => undefined)),
  state: brazilianStateSchema.optional().or(z.literal("").transform(() => undefined)),
  profession: z.string().trim().max(100).optional().or(z.literal("").transform(() => undefined)),
  howDidMeet: z.string().trim().max(100).optional().or(z.literal("").transform(() => undefined)),
  notes: z.string().trim().max(5000).optional().or(z.literal("").transform(() => undefined)),
  /** Estrangeiro, sem CPF: dispensa o CPF quando a clínica o exige. */
  semCpf: z.boolean().optional(),
});
export type PatientInviteAcceptInput = z.infer<typeof patientInviteAcceptSchema>;

